import { Box, Divider, Tab, Tabs } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TabPanel from "../../components/common/TabPanel";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useNatGateway } from "../../hooks/useNatGateway";
import { initAuto4clientDataClassification, initAuto4clientDataPreparation } from "../../services/aws/ecs";
import ActionButtons from "./components/ActionButtons";
import DataClassification from "./components/DataClassification";
import DataPreparation from "./components/DataPreparation";
import useCompanyNames from "./hooks/useCompanyNames";
import useMongoTaskData from "./hooks/useMongoTaskData";

function Auto4Client() {
  const navigate = useNavigate();
  const [tabSection, setTabSection] = useState("data-preparation");
  const { alertState, showAlert, closeAlert } = useAlert();
  const {
    isReady: natGatewayReady,
    isLoading: natGatewayLoading,
    checkStatus,
    createGateway,
  } = useNatGateway("scraper-natgw");
  const { taskData: mongoTaskData } = useMongoTaskData();
  const { companyNames } = useCompanyNames();
  const [dataPreparationValues, setDataPreparationValues] = useState({
    allCompaniesSelected: true,
    selectedCompany: null,
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
  });
  const [dataClassificationInputs, setDataClassificationInputs] = useState({
    mongoTaskID: "",
  });

  const validateInputs = () => {
    switch (tabSection) {
      case "data-preparation":
        if (!dataPreparationValues.allCompaniesSelected && dataPreparationValues.selectedCompany === null) {
          showAlert("error", "Invalid Company!");
          return false;
        }
        if (dataPreparationValues.startDate >= dataPreparationValues.endDate) {
          showAlert("error", "Start Date Must Be Before End Date!");
          return false;
        }
        return true;

      case "data-classification":
        if (!dataClassificationInputs.mongoTaskID) {
          showAlert("error", "Please select a Task ID!");
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // Handlers
  const handleSubmitTask = async () => {
    if (!validateInputs()) {
      return;
    }

    let res = null;
    switch (tabSection) {
      case "data-preparation":
        res = await initAuto4clientDataPreparation(
          dataPreparationValues.selectedCompany,
          dataPreparationValues.startDate,
          dataPreparationValues.endDate
        );
        if (res.status === 200) {
          showAlert("success", "Success!");
          setTimeout(() => {
            navigate("/tasks");
          }, 3000);
        }
        break;

      case "data-classification":
        const companyName = mongoTaskData.filter((x) => x["task-id"] === dataClassificationInputs.mongoTaskID)[0][
          "company-name"
        ];
        const startDate = mongoTaskData.filter((x) => x["task-id"] === dataClassificationInputs.mongoTaskID)[0][
          "start-date"
        ];
        const endDate = mongoTaskData.filter((x) => x["task-id"] === dataClassificationInputs.mongoTaskID)[0][
          "end-date"
        ];
        const platforms =
          "'Review Platform Data' 'Forum Data' 'Instagram/Twitter Data' 'Linkedin/Facebook Data' 'China/Russia Data'";
        res = await initAuto4clientDataClassification(dataClassificationInputs.mongoTaskID, companyName, platforms, startDate, endDate);
        if (res.status === 200) {
          showAlert("success", "Success!");
          setTimeout(() => {
            navigate("/tasks");
          }, 3000);
        }
        break;

      default:
        break;
    }
  };

  const handleCreateNatGateway = async () => {
    showAlert("info", "Creating NAT Gateway, please wait...");
    createGateway();
  };

  const handleChangeTab = (event, newValue) => setTabSection(newValue);
  const handleDataPreparationChange = (newInputs) => setDataPreparationValues(newInputs);
  const handleDataClassificationChange = (newInputs) => setDataClassificationInputs(newInputs);

  // Check NAT Gateway status on component mount
  useEffect(() => {
    checkStatus();
  }, []);

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      {/* Top bar */}
      <TopBarGridContainer>
        <Grid>
          <Title text={"AUTO4CLIENT"} sx={{ my: 5 }} />
        </Grid>
        <Grid size="grow">
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              mt: 5,
            }}
          >
            {/* Tab selection */}
            <Tabs value={tabSection} sx={{ pr: 10 }} onChange={handleChangeTab}>
              <Tab label="Data Preparation" value={"data-preparation"} />
              <Tab label="Data Classification" value={"data-classification"} />
            </Tabs>

            {/* Action Buttons */}
            <ActionButtons
              onSubmit={handleSubmitTask}
              onCreateGateway={handleCreateNatGateway}
              natGatewayReady={natGatewayReady}
              natGatewayLoading={natGatewayLoading}
            />
          </Box>
        </Grid>
      </TopBarGridContainer>
      <Divider variant="middle" />

      {/* Using TabPanel components instead of conditional rendering */}
      <TabPanel value={tabSection} index="data-preparation">
        <DataPreparation companyNames={companyNames} onInputsChange={handleDataPreparationChange} />
      </TabPanel>

      <TabPanel value={tabSection} index="data-classification">
        <DataClassification mongoTaskData={mongoTaskData} onInputsChange={handleDataClassificationChange} />
      </TabPanel>
    </Layout>
  );
}

export default Auto4Client;
